import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import api from "../../services/api";

const SalesManagement = () => {
  const { storeId } = useParams();
  const [period, setPeriod] = useState("daily");
  const [salesData, setSalesData] = useState([]);
  const [topSellingItems, setTopSellingItems] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    averageOrderValue: 0,
    totalOrders: 0,
    cancelledOrders: 0,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, period]);

  const formatPeriodData = (data) => {
    return data.map((item) => {
      let name = "";
      // Supabase format: { date: "2025-12-20", ... }
      if (item.date) {
        const d = new Date(item.date);
        name = `${d.getMonth() + 1}/${d.getDate()}`;
      } else if (item.week) {
        name = `${item.year || new Date().getFullYear()}W${item.week}`;
      } else if (item.month && item.year) {
        name = `${item.year}/${item.month}`;
      }
      // MongoDB format: { _id: { day, month, year }, ... }
      else if (typeof item._id === "string") {
        name = item._id;
      } else if (item._id) {
        if (item._id.day !== undefined) {
          name = `${item._id.month}/${item._id.day}`;
        } else if (item._id.week !== undefined) {
          name = `${item._id.year}W${item._id.week}`;
        } else if (item._id.month !== undefined) {
          name = `${item._id.year}/${item._id.month}`;
        }
      }
      return {
        name,
        sales: item.totalSales || item.grandTotal || 0,
        orders: item.orderCount || 0,
      };
    });
  };

  const fetchSales = async () => {
    if (!storeId) return;
    try {
      setError(null);
      setLoading(true);
      const params = {
        startDate: new Date(
          new Date().setDate(new Date().getDate() - 6)
        ).toISOString(),
        endDate: new Date().toISOString(),
        storeId,
      };

      const periodRes = await api.get(`/api/reports/${period}`, { params });
      const mappedSales = Array.isArray(periodRes.data)
        ? formatPeriodData(periodRes.data)
        : [];
      setSalesData(mappedSales);

      const productRes = await api.get(`/api/reports/products`, { params });
      const products = Array.isArray(productRes.data)
        ? productRes.data.map((p) => ({
            name: p.productName || p.name,
            quantity: p.totalQuantity,
            amount: p.totalSales,
          }))
        : [];
      setTopSellingItems(products);

      const total = mappedSales.reduce((sum, s) => sum + s.sales, 0);
      const orders = mappedSales.reduce((sum, s) => sum + s.orders, 0);
      setSummaryData({
        totalSales: total,
        averageOrderValue: orders ? Math.round(total / orders) : 0,
        totalOrders: orders,
        cancelledOrders: 0,
      });
    } catch (err) {
      console.error("売上データの取得に失敗しました:", err);
      setError("売上データの取得に失敗しました");
      setSalesData([]);
      setTopSellingItems([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box aria-busy={loading}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h5" gutterBottom>
        売上管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 期間選択 */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>期間</InputLabel>
          <Select
            value={period}
            label="期間"
            onChange={(e) => setPeriod(e.target.value)}
            disabled={loading}
          >
            <MenuItem value="daily">日次</MenuItem>
            <MenuItem value="weekly">週次</MenuItem>
            <MenuItem value="monthly">月次</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* サマリーカード */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                総売上
              </Typography>
              <Typography variant="h4">
                ¥{summaryData.totalSales.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                平均注文金額
              </Typography>
              <Typography variant="h4">
                ¥{summaryData.averageOrderValue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                注文数
              </Typography>
              <Typography variant="h4">{summaryData.totalOrders}件</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                キャンセル数
              </Typography>
              <Typography variant="h4">
                {summaryData.cancelledOrders}件
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 売上推移 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          売上推移
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日付</TableCell>
                <TableCell align="right">売上</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                salesData.map((data) => (
                  <TableRow key={data.name}>
                    <TableCell>{data.name}</TableCell>
                    <TableCell align="right">
                      ¥{data.sales.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 売上ランキング */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          売上ランキング
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>商品名</TableCell>
                <TableCell align="right">販売数</TableCell>
                <TableCell align="right">売上金額</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                topSellingItems.map((item) => (
                  <TableRow key={item.name}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">
                      ¥{item.amount.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SalesManagement;
